import { CharacterData } from "../../../../utils/game/character/CharacterData";
import { Blue, Red, Yellow } from "../../../../utils/sys/Color";
import { CharacterDataParser } from "../../../../utils/game/character/CharacterDataParser";
import { DynamicInventoryDisplayMode } from "../InventoryItemCollection/InventoryItemDynamicCollection";
import { KeyValuePair } from "../../../../utils/sys/KeyValuePair";
import { ReactElement } from "react";
import { useMemo, useState } from "react";
import { WieldStyle } from "../../../../utils/game/mechanics/WieldStyle";
import { WieldMode } from "../../../../utils/game/mechanics/WieldMode";
import { InventoryItemCategory } from "../../../../utils/sys/InventoryItemCategory";

import AttributesContainer from "../AttributesContainer/AttributesContainer";
import BorderedPanel from "../../../../common/components/display/BorderedPanel/BorderedPanel";
import CharacterSheetAbilitiesTab from "./CharacterSheetAbilitiesTab/CharacterSheetAbilitiesTab";
import CharacterSheetInfoTab from "./CharacterSheetInfoTab/CharacterSheetInfoTab";
import CharacterSheetInventoryTab from "./CharacterSheetInventoryTab/CharacterSheetInventoryTab";
import CharacterSheetNotesTab from "./CharacterSheetNotesTab/CharacterSheetNotesTab";
import CharacterSheetTalentsTab from "./CharacterSheetTalentsTab/CharacterSheetTalentsTab";
import CharacterSheetTraitsTab from "./CharacterSheetTraitsTab/CharacterSheetTraitsTab";
import CharacterHeader from "../CharacterHeader/CharacterHeader";
import CombatStatsPanel from "../CombatStatsPanel/CombatStatsPanel";
import Flexbox from "../../../../common/components/layout/Flexbox/Flexbox";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import Meter from "../../../../common/components/input/Meter/Meter";
import MouseTrackingComponent from "../../../../common/components/wrappers/MouseTrackingComponent/MouseTrackingComponent";
import NumberInputToggleField from "../../../../common/components/input/NumberInputToggleField/NumberInputToggleField";
import ProficienciesContainer from "../ProficienciesContainer/ProficienciesContainer";
import ResistancesContainer from "../ResistancesContainer/ResistancesContainer";
import StatBoard from "../../../../utils/game/mechanics/StatBoard";
import TabControl from "../../../../common/components/layout/TabControl/TabControl";
import TextField from "../../../../common/components/display/TextField/TextField";
import Spinner from "../../../../common/components/system/Spinner/Spinner";
import WikiArticleDrawer from "../WikiArticleDrawer/WikiArticleDrawer";

import "./CharacterSheet_style.css";

const statBoard = new StatBoard();

type Props = {
    characterData: CharacterData;
    isEditable?: boolean;
    isRefreshing?: boolean;
    useIconAttributes?: boolean;
    onCharacterDataValueChanged?: (vals: KeyValuePair[]) => void;
};

export default function CharacterSheet({
    characterData,
    isEditable = false,
    isRefreshing,
    useIconAttributes = true,
    onCharacterDataValueChanged,
}: Props): ReactElement {
    const [dmgDisplayMode, setDmgDisplayMode] = useState<
        "physical" | "magic"
    >("physical");
    const [selectedFeaturesTab, setSelectedFeaturesTab] = useState<
        string | null
    >(null);
    const [selectedInfoTab, setSelectedInfoTab] = useState<string | null>(null);
    const [inventoryDisplayMode, setInventoryDisplayMode] =
        useState<DynamicInventoryDisplayMode>("list");
    const [selectedAbilitiesTab, setSelectedAbilitiesTab] = useState<
        string | null
    >(null);
    const [selectedNotesTab, setSelectedNotesTab] = useState<string | null>(
        null
    );
    const [talentPointSpendEnabled, setTalentPointSpendEnabled] =
        useState<boolean>(false);
    const [pendingTalentPointSpend, setPendingTalentPointSpend] = useState<
        Array<{ id: number; points: number }>
    >([]);
    const [schoolPointSpendEnabled, setSchoolPointSpendEnabled] =
        useState<boolean>(false);
    const [pendingSchoolPointSpend, setPendingSchoolPointSpend] = useState<
        Array<{ id: number; points: number }>
    >([]);
    const [equipmentExpanded, setEquipmentExpanded] = useState<boolean>(true);
    const [inventoryExpanded, setInventoryExpanded] = useState<boolean>(true);
    const [walletExpanded, setWalletExpanded] = useState<boolean>(true);

    const [isEditingTalents, setEditingTalents] = useState<boolean>(false);
    const [isEditingSchools, setEditingSchools] = useState<boolean>(false);

    const [articleDrawerTarget, setArticleDrawerTarget] = useState<string | number | null>(null);

    CharacterDataParser.parseCharacterDataToStatBoard(characterData, statBoard);

    const validWieldModes: ReadonlyArray<WieldMode> = useMemo(() => {
        
        const isLHWeapon = characterData.lh?.type === InventoryItemCategory.WEAPON;
        const isRHWeapon = characterData.rh?.type === InventoryItemCategory.WEAPON;

        const isUnarmed = !isLHWeapon && !isRHWeapon;

        if(isUnarmed) {
            return [WieldMode.RIGHT_HAND_WIELD];
        }

        const validModes: Array<WieldMode> = [];

        if(isLHWeapon && isRHWeapon) {
            if(characterData.lh?.permitsWieldStyle(WieldStyle.ONE_HAND)) {
                validModes.push(WieldMode.LEFT_HAND_WIELD);
            }
            if(characterData.rh?.permitsWieldStyle(WieldStyle.ONE_HAND)) {
                validModes.push(WieldMode.RIGHT_HAND_WIELD);
            }
            if(characterData.lh?.permitsWieldStyle(WieldStyle.ONE_HAND) && characterData.rh?.permitsWieldStyle(WieldStyle.ONE_HAND)) {
                validModes.push(WieldMode.DUAL_WIELD);
            }
        } else if(isLHWeapon) {
            if(characterData.lh?.permitsWieldStyle(WieldStyle.ONE_HAND)) {
                validModes.push(WieldMode.LEFT_HAND_WIELD);
            }
            if(characterData.lh?.permitsWieldStyle(WieldStyle.TWO_HAND)) {
                validModes.push(WieldMode.TWO_HAND_WIELD);
            }
        } else {
            if(characterData.rh?.permitsWieldStyle(WieldStyle.ONE_HAND)) {
                validModes.push(WieldMode.RIGHT_HAND_WIELD);
            }
            if(characterData.rh?.permitsWieldStyle(WieldStyle.TWO_HAND)) {
                validModes.push(WieldMode.TWO_HAND_WIELD);
            }
        }

        return validModes;
    }, [characterData.lh, characterData.rh]);

    return (
        <Flexbox direction="column" className="character-sheet">
            <div className="character-sheet-row-span-2 no-padding">
                <CharacterHeader
                    characterData={characterData}
                    endContent={isRefreshing && <Spinner />}
                />
            </div>
            {articleDrawerTarget == null ? 
                null : 
                (<WikiArticleDrawer 
                    alignment="right" 
                    isVisible={true} 
                    articleID={typeof articleDrawerTarget === "number" ? articleDrawerTarget : undefined}
                    articleTitle={typeof articleDrawerTarget === "string" ? articleDrawerTarget : undefined}
                    onClose={() => {setArticleDrawerTarget(null);}}
                />)
            }
            <FlexRow wrap={true} className="profs-and-vitality-row">
                <div className="character-sheet-row-span-1">
                    <ProficienciesContainer
                        characterData={characterData}
                        statBoard={statBoard}
                        isEditable={isEditable}
                        onCharacterDataValueChanged={
                            onCharacterDataValueChanged
                        }
                        onProficiencyNameClick={(proficiencyName) => {
                            setArticleDrawerTarget(proficiencyName);
                        }}
                    />
                </div>
                <div className="character-sheet-row-span-1">
                    <Meter
                        fillColor={Red}
                        currentValue={characterData.currentHP}
                        maxValue={characterData.maxHP}
                        meterClassName="hp-meter"
                        isEditable={isEditable}
                        isCurrentEditable={true}
                        isMaxEditable={true}
                        onValuesChanged={(vals) => {
                            if (
                                vals.current !== characterData.currentHP ||
                                vals.max !== characterData.maxHP
                            ) {
                                onCharacterDataValueChanged?.([
                                    {
                                        key: "hp_current",
                                        value: vals.current,
                                    },
                                    { key: "hp_max", value: vals.max },
                                ]);
                            }
                        }}
                    />
                    <FlexRow gap={8}>
                        <Meter
                            fillColor={Yellow}
                            currentValue={Number.parseInt(
                                statBoard.getValueByStatKey("EP")
                            )}
                            maxValue={Number.parseInt(
                                statBoard.getValueByStatKey("EPMAX")
                            )}
                            meterClassName="ep-meter"
                        />
                        <NumberInputToggleField
                            className="ep-meter-adjust"
                            label="Adj"
                            value={characterData.epAdjust}
                            onStopEditing={(val) => {
                                onCharacterDataValueChanged?.([
                                    { key: "stamina_adj", value: val },
                                ]);
                            }}
                        ></NumberInputToggleField>
                        <div className="exhaustion-label">
                            <TextField type="bold">Exhaustion</TextField>
                        </div>
                    </FlexRow>
                    <FlexRow gap={8}>
                        <Meter
                            fillColor={Blue}
                            currentValue={Number.parseInt(
                                statBoard.getValueByStatKey("MP")
                            )}
                            maxValue={Number.parseInt(
                                statBoard.getValueByStatKey("MPMAX")
                            )}
                            meterClassName="mp-meter"
                        />
                        <NumberInputToggleField
                            className="mp-meter-adjust"
                            label="Adj"
                            value={characterData.mpAdjust}
                            onStopEditing={(val) => {
                                onCharacterDataValueChanged?.([
                                    { key: "channel_adj", value: val },
                                ]);
                            }}
                        ></NumberInputToggleField>
                        <div className="exhaustion-col">
                            <NumberInputToggleField
                                value={characterData.exhaustion}
                                onStopEditing={(val) => {
                                    onCharacterDataValueChanged?.([
                                        { key: "exhaust", value: val },
                                    ]);
                                }}
                                displayFieldStyle={{ textAlign: "center" }}
                            ></NumberInputToggleField>
                        </div>
                    </FlexRow>
                </div>
            </FlexRow>
            <FlexRow wrap={true} className="stats-row">
                <div className="character-sheet-row-span-1">
                    <CombatStatsPanel
                        statBoard={statBoard}
                        characterData={characterData}
                        dmgDisplayMode={dmgDisplayMode}
                        validWieldModes={validWieldModes}
                        wieldMode={characterData.wieldMode}
                        useIconAttributes={useIconAttributes} 
                        onAttributeTitleClick={(attributeName: string) => {
                            setArticleDrawerTarget(attributeName);
                        }}
                        onDmgDisplayModeChange={setDmgDisplayMode}
                        onWieldModeChange={(wieldMode) => {
                            onCharacterDataValueChanged?.([{key: "wield_mode", value: wieldMode}]);
                        }}
                    />
                    <FlexRow alignment="center">
                        <BorderedPanel borderType={2} style={{ width: "100%" }}>
                            <AttributesContainer
                                alignment="center"
                                statBoard={statBoard}
                                onAttributeTitleClick={(attributeName) => {
                                    setArticleDrawerTarget(attributeName);
                                }}
                            />
                        </BorderedPanel>
                    </FlexRow>
                    <FlexRow alignment="center">
                        <BorderedPanel borderType={2} style={{ width: "100%" }}>
                            <ResistancesContainer
                                alignment="center"
                                characterData={characterData}
                                statBoard={statBoard}
                                onResistanceTitleClick={(resistanceName) => {
                                    setArticleDrawerTarget(`${resistanceName} Resistance`);
                                }}
                            />
                        </BorderedPanel>
                    </FlexRow>
                </div>
                <div className="character-sheet-row-span-1">
                    <div className="features-container">
                        <TabControl
                            id="character_sheet_feature_tab_control"
                            selectedTab={selectedFeaturesTab}
                            onSelectedTabChange={setSelectedFeaturesTab}
                            tabs={[
                                { id: "info", label: "Info" },
                                { id: "traits", label: "Traits" },
                                { id: "talents", label: "Talents" },
                                { id: "inventory", label: "Inventory" },
                                { id: "abilities", label: "Abilities" },
                                { id: "notes", label: "Notes" },
                            ]}
                        />
                        <hr style={{ margin: "8px 0" }} />
                        <div className="features-content-panel">
                            {selectedFeaturesTab === "info" && (
                                <CharacterSheetInfoTab
                                    characterData={characterData}
                                    selectedTab={selectedInfoTab}
                                    onSelectedTabChanged={setSelectedInfoTab}
                                    isEditable={isEditable}
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                />
                            )}
                            {selectedFeaturesTab === "traits" && (
                                <CharacterSheetTraitsTab
                                    characterData={characterData}
                                    statBoard={statBoard}
                                    isEditable={isEditable}
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                />
                            )}
                            {selectedFeaturesTab === "talents" && (
                                <CharacterSheetTalentsTab
                                    characterData={characterData}
                                    statBoard={statBoard}
                                    isEditable={isEditable}
                                    isEditing={isEditingTalents}
                                    onEditingChanged={(editing) => {
                                        setEditingTalents(editing);
                                    }}
                                    pointSpendEnabled={talentPointSpendEnabled}
                                    onPointSpendEnabledChanged={
                                        setTalentPointSpendEnabled
                                    }
                                    pendingTalentPointSpend={
                                        pendingTalentPointSpend
                                    }
                                    onPendingTalentPointSpendChanged={
                                        setPendingTalentPointSpend
                                    }
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                />
                            )}
                            {selectedFeaturesTab === "abilities" && (
                                <CharacterSheetAbilitiesTab
                                    characterData={characterData}
                                    selectedTab={selectedAbilitiesTab}
                                    onSelectedTabChanged={
                                        setSelectedAbilitiesTab
                                    }
                                    isEditable={isEditable}
                                    isEditing={isEditingSchools}
                                    onEditingChanged={setEditingSchools}
                                    pointSpendEnabled={schoolPointSpendEnabled}
                                    onPointSpendEnabledChanged={
                                        setSchoolPointSpendEnabled
                                    }
                                    pendingSchoolPointSpend={
                                        pendingSchoolPointSpend
                                    }
                                    onPendingSchoolPointSpendChanged={
                                        setPendingSchoolPointSpend
                                    }
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                />
                            )}
                            {selectedFeaturesTab === "inventory" && (
                                <CharacterSheetInventoryTab
                                    characterData={characterData}
                                    isEditable={isEditable}
                                    inventoryDisplayMode={inventoryDisplayMode}
                                    onInventoryDisplayModeChanged={
                                        setInventoryDisplayMode
                                    }
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                    equipmentExpanded={equipmentExpanded}
                                    onEquipmentExpandedChanged={
                                        setEquipmentExpanded
                                    }
                                    inventoryExpanded={inventoryExpanded}
                                    onInventoryExpandedChanged={
                                        setInventoryExpanded
                                    }
                                    walletExpanded={walletExpanded}
                                    onWalletExpandedChanged={setWalletExpanded}
                                />
                            )}
                            {selectedFeaturesTab === "notes" && (
                                <CharacterSheetNotesTab
                                    characterData={characterData}
                                    selectedTab={selectedNotesTab}
                                    onSelectedTabChanged={setSelectedNotesTab}
                                    isEditable={isEditable}
                                    onCharacterDataValueChanged={
                                        onCharacterDataValueChanged
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            </FlexRow>
            <MouseTrackingComponent enabled={false} offset={{ x: 10, y: 10 }} />
        </Flexbox>
    );
}
